

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/login',
  '/logout',
  '/',
  '/about',
  '/contact',
  '/gallery',
  '/book',
];

// Routes that should redirect to dashboard if already authenticated
const AUTH_ONLY_ROUTES = ['/login'];

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (loading) return;

    const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname) || 
      location.pathname.startsWith('/_next') ||
      location.pathname.startsWith('/static') ||
      location.pathname.includes('.');
    
    const isAuthOnlyRoute = AUTH_ONLY_ROUTES.includes(location.pathname);

    // Redirect authenticated users away from login page
    if (isAuthOnlyRoute && user) {
      const redirect = searchParams.get('redirect') || '/dashboard';
      navigate(redirect, { replace: true });
      return;
    }

    // Redirect unauthenticated users to login
    if (!isPublicRoute && !user) {
      const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname)}`;
      navigate(loginUrl, { replace: true });
      return;
    }

    setIsReady(true);
  }, [user, loading, location.pathname, navigate, searchParams]);

  // Show loading state while checking auth
  if (loading || !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          <p className="text-stone-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
